import { graphql } from '#features/api';
import {
  BungieIcon,
  DiscordIcon,
  EpicGamesIcon,
  GoogleIcon,
  RiotGamesIcon,
  SteamIcon,
  XboxIcon,
} from '#features/ui';
import { AppleIcon } from '../components/AppleSignInButton/AppleIcon';
import { PROVIDER_FULL_NAMES } from './providerLabel';

// VK is retired: the gateway still knows the value (old accounts keep their link in the
// database), the UI no longer offers or renders it anywhere.
export type OAuthProvider = Exclude<ReturnType<typeof graphql.scalar<'OauthType'>>, 'VK'>;

export const OAuthProvider = {
  DISCORD: 'DISCORD',
  GOOGLE: 'GOOGLE',
  STEAM: 'STEAM',
  EPIC_GAMES: 'EPIC_GAMES',
  RIOT_GAMES: 'RIOT_GAMES',
  BUNGIE: 'BUNGIE',
  XBOX: 'XBOX',
  APPLE: 'APPLE',
} as const satisfies Record<OAuthProvider, OAuthProvider>;

// `discord.com/users/${id}`
// `https://steamcommunity.com/profiles/${id}`

export const OAuthProviders: Record<
  OAuthProvider,
  {
    fullName: string;
    icon: React.ElementType;
    generateProfileLink?: (profile: { id: string; displayName?: string | null }) => string;
  }
> = {
  [OAuthProvider.APPLE]: { fullName: PROVIDER_FULL_NAMES.APPLE, icon: AppleIcon },
  [OAuthProvider.DISCORD]: {
    fullName: PROVIDER_FULL_NAMES.DISCORD,
    icon: DiscordIcon,
    generateProfileLink: ({ id, displayName }) =>
      displayName ? displayName : `https://discord.com/users/${id}`,
  },
  [OAuthProvider.GOOGLE]: {
    fullName: PROVIDER_FULL_NAMES.GOOGLE,
    icon: GoogleIcon,
  },
  [OAuthProvider.STEAM]: {
    fullName: PROVIDER_FULL_NAMES.STEAM,
    icon: SteamIcon,
    generateProfileLink: ({ id }) => `https://steamcommunity.com/profiles/${id}`,
  },
  [OAuthProvider.EPIC_GAMES]: {
    fullName: PROVIDER_FULL_NAMES.EPIC_GAMES,
    icon: EpicGamesIcon,
  },
  [OAuthProvider.RIOT_GAMES]: {
    fullName: PROVIDER_FULL_NAMES.RIOT_GAMES,
    icon: RiotGamesIcon,
  },
  // Вход через Bungie заводит ещё и игровую личность Destiny: бэкенд по этой привязке начинает
  // собирать статистику (Power, класс, playtime, K/D в PvP). Ссылки на профиль нет намеренно —
  // публичной страницы у bungie.net под наш id нет.
  [OAuthProvider.BUNGIE]: {
    fullName: PROVIDER_FULL_NAMES.BUNGIE,
    icon: BungieIcon,
  },
  // Xbox привязывается ради игровой личности на консоли: внешний id — это XUID, а displayName —
  // gamertag. Ссылки на профиль нет намеренно: публичной страницы под XUID у Microsoft нет,
  // а поиск по gamertag ведёт в чужой аккаунт (тег не уникален и переиспользуется).
  [OAuthProvider.XBOX]: {
    fullName: PROVIDER_FULL_NAMES.XBOX,
    icon: XboxIcon,
  },
};

export { getProviderLabel, PROVIDER_FULL_NAMES } from './providerLabel';
